import compact from 'lodash/compact';
import partition from 'lodash/partition';

import {
    EntryPdfSignatureQuery,
    EntryPrintFormTableCellParams,
    TableCellParams,
    EntryPrintFormVaccinationParams,
    EntryPrintFormPairParams,
} from 'chaika-types';
import {
    DiagnosisData,
    EntryPrintFormBlockData,
    EntryPrintFormFieldOptions,
    EntryPrintFormFieldsData,
    EntryPrintFormSectionData,
    EntryTypeFieldName,
    FrontendDictionaryNames,
    type LaboratoryTestResult,
    LaboratoryTestStatus,
    type Optional,
    TaskStatus,
} from 'chaika-frontend-types/frontend-cms';

import { store } from '@store';
import { queryClient } from '@store/queryClient';
import { getCachedUserById } from '@user/queries/userQueries';
import { getFullName } from '@user/utils/getFullName';
import { Entry } from '@patient/store/entry/Entry';
import { DictionaryNames, EntryTypeFieldType, TableFieldCellType } from 'chaika-enums';

import { EntryTypeSection } from '@template/store/entryType/EntryTypeSection';
import { EntryTypeBlock } from '@template/store/entryType/EntryTypeBlock';
import { EntryTypeField } from '@template/store/entryType/EntryTypeField';
import { ENTRY_FIELD_EXCEPTIONS } from '@template/store/entryType/types';
import { isDefined } from '@utils/isDefined';
import { getTranslatesForAllLocales, translate } from '@chaika/i18n/helper';
import { getElementWidthReport } from '@components/EntryTypeElement/getElementWidth';
import { DrugField, ReferralField, TableField } from '@store/models/EntryFields';
import { TableData } from '@components/Fields/Table/types';
import { OptionType } from '@components/Select';
import { Dictionary } from '@template/store/dictionary/Dictionary';
import { DateFormat, DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';
import { RecommendationList } from '@store/models/SystemFields/MedicalCase/RecommendationList';
import { DateType } from 'chaika-utils';
import {
    filterEntryOutwardReferrals,
    makeGroupedItems,
    splitReferralsByReservation,
} from '@common/TreatmentPlan/utils/treatmentPlanItems';
import { getFilteredDrugs } from '@common/TreatmentPlan/utils/filters';
import { MedicalCase } from '@store/models/SystemFields/MedicalCase';
import { taskStore } from '@store/Task';
import { ItemType } from '@common/TreatmentPlan/types';
import { formatItemDate } from '@common/TreatmentPlan/utils/treatmentPlanData';
import { LaboratoryTestTableDataHelper } from '@store/models/SystemFields/LaboratoryTest/LaboratoryTestTableDataHelper';
import { ENTRY_EPISODES_BLOCK_ID, ENTRY_GRADE_BLOCK_ID, ENTRY_TREATMENT_PLAN_BLOCK_ID } from '@store/types';
import { DentalField, DentalFieldData } from '@store/models/EntryFields';
import { buildDentalPrintData } from '@patient/services/entry/entryPrintFormDentalHelper';
import {
    LEGACY_DENTAL_FIELD_NAME,
    pickFilledFormula,
    resolveDentalFieldValue,
} from '@patient/ui/SoapEntryPage/components/Template/SystemBlock/DentalTreatment/utils/resolveDentalFieldValue';
import { getTranslate } from '@chaika/i18n/helper';

interface Params {
    isSoap?: boolean;
    isShortPrintForm?: boolean;
    ignoreHideFromPatient?: boolean;
    signature?: EntryPdfSignatureQuery;
}

interface EntryPrintFormTableCellParamsWithName extends EntryPrintFormTableCellParams {
    name: string;
    type: TableFieldCellType;
}

export const includedIds: string[] = [ENTRY_TREATMENT_PLAN_BLOCK_ID, ENTRY_GRADE_BLOCK_ID];
export const hiddenBlockIds = [ENTRY_EPISODES_BLOCK_ID];

export class EntryPrintFormParamsHelper {
    private static entry: Entry = store.entry;
    private static dictionary: Dictionary = store.dictionary;

    private static hideSectionTitles: Set<string> = new Set(
        [
            ...getTranslatesForAllLocales('episodes'),
            ...getTranslatesForAllLocales('services'),
            'данные из медиалога',
        ].map(name => name.toLowerCase()),
    );

    private static includedBlockTitles: string[] = [
        ...getTranslatesForAllLocales('resultOfCut'),
        ...getTranslatesForAllLocales('insuranceCut'),
        ...getTranslatesForAllLocales('conclusionCut'),
    ].map(title => title.toLowerCase());

    public static makeSectionParams(
        { title, blocks, subTitle }: Pick<EntryTypeSection, 'title' | 'blocks'> & { subTitle?: string },
        params: Params,
    ): EntryPrintFormSectionData {
        return {
            title: this.hideSectionTitles.has(title.toLowerCase()) ? undefined : title,
            blocks: blocks
                .filter(item => this.isBlockShowOnPrint(item, params))
                .flatMap(block => this.makeBlockParams(block, params)),
        };
    }

    private static isBlockShowOnPrint(
        block: EntryTypeBlock,
        { isShortPrintForm, ignoreHideFromPatient }: Params,
    ): boolean {
        const blockWithUsedMedications = block.fieldsWithData.find(
            field => field.type === EntryTypeFieldType.UsedMedications,
        );
        const blockHasUsedMedications = !!blockWithUsedMedications;

        return (
            getTranslatesForAllLocales('services').every(title => block.title.toLowerCase() !== title.toLowerCase()) &&
            (blockHasUsedMedications ? block.hasSpecialActiveData : block.hasData || block.isEnableOnViewMode) &&
            !hiddenBlockIds.includes(block.id) &&
            (ignoreHideFromPatient
                ? true
                : !this.entry.entryTypeItemPresentationList.getByItemId(block.id)?.hideFromPatient) &&
            (isShortPrintForm
                ? !(
                      this.includedBlockTitles.every(name => !block.title.toLowerCase().includes(name)) &&
                      includedIds.every(includedId => includedId !== block.id) &&
                      !block.fieldsWithData.some(({ name }) =>
                          ENTRY_FIELD_EXCEPTIONS.includes(name as EntryTypeFieldType),
                      )
                  )
                : true)
        );
    }

    private static makeBlockParams(
        block: EntryTypeBlock,
        { ignoreHideFromPatient }: Params,
    ): EntryPrintFormBlockData[] {
        const { isMedicalCasesHiddedOnPrint } = this.entry;
        const isSingleField = block.fields.length === 1;
        const dentalFields = block.fields.filter(
            field =>
                field.type === EntryTypeFieldType.DentalTreatment &&
                !block.fieldsWithData.some(it => it.name === field.name),
        );
        const augmentedFieldsWithData = [...block.fieldsWithData, ...dentalFields];
        const showOnPrintFields = augmentedFieldsWithData.filter(field =>
            this.isFieldShowOnPrint(field, { ignoreHideFromPatient: isSingleField || ignoreHideFromPatient }),
        );

        const [[medicalCaseField], fieldsWithData] = partition<EntryTypeField>(
            showOnPrintFields,
            field => field.type === EntryTypeFieldType.EntryMedicalCases,
        );

        const [[laboratoryTestField], fields] = partition<EntryTypeField>(
            fieldsWithData,
            field => field.type === EntryTypeFieldType.LaboratoryTests,
        );

        const printBlock = {
            title: block.title !== '' ? block.title : undefined,
            fields: fields.flatMap(field => this.makeFieldParams(field)),
        };

        const blocks = isDefined(laboratoryTestField)
            ? [printBlock, ...this.makeEntryLabTestBlockParams()]
            : [printBlock];

        return isDefined(medicalCaseField) && !isMedicalCasesHiddedOnPrint
            ? [...blocks, ...this.makeEntryMedicalCasesBlockParams()]
            : blocks;
    }

    private static isFieldShowOnPrint(field: EntryTypeField, { ignoreHideFromPatient }: Params): boolean {
        return ignoreHideFromPatient
            ? true
            : !this.entry.entryTypeItemPresentationList.getByItemId(field.name)?.hideFromPatient;
    }

    private static makeFieldParams(field: EntryTypeField): EntryPrintFormFieldsData {
        let value: EntryPrintFormFieldsData['value'];
        let isHtml: EntryPrintFormFieldsData['isHtml'];
        let tableData: EntryPrintFormFieldsData['tableData'];
        let vaccinationData: EntryPrintFormFieldsData['vaccinationData'];
        let dentalData: EntryPrintFormFieldsData['dentalData'];
        let titleOverride: string | undefined;
        let type:
            | EntryTypeFieldType.Table
            | EntryTypeFieldType.EntryMedicalCases
            | EntryTypeFieldType.Vaccinations
            | EntryTypeFieldType.DentalTreatment
            | undefined;
        const fieldOptions: EntryPrintFormFieldOptions = {} as EntryPrintFormFieldOptions;
        fieldOptions.occupiedLineWidth = getElementWidthReport(field.width);

        switch (field.type) {
            case EntryTypeFieldType.DentalTreatment: {
                type = EntryTypeFieldType.DentalTreatment;
                const isInherent = !!field.data.inherent;
                const fieldValue = resolveDentalFieldValue({
                    ownValue: pickFilledFormula(
                        this.getDentalFieldValue(field.name),
                        this.getDentalFieldValue(LEGACY_DENTAL_FIELD_NAME),
                    ),
                    inheritedValue: isInherent
                        ? pickFilledFormula(
                              this.entry.getEhrFieldValue<DentalFieldData>(field.name),
                              this.entry.getEhrFieldValue<DentalFieldData>(LEGACY_DENTAL_FIELD_NAME),
                          )
                        : undefined,
                    isInherent,
                });
                const selectedLang = this.entry.selectedLang;

                dentalData = buildDentalPrintData(fieldValue ?? ({ permanent: {}, baby: {} } as DentalFieldData), key =>
                    getTranslate(key, selectedLang),
                );
                titleOverride = getTranslate('dentalFormula', selectedLang);

                break;
            }
            case EntryTypeFieldType.Allergy:
            case EntryTypeFieldType.CheckboxList:
            case EntryTypeFieldType.CheckboxWithText:
            case EntryTypeFieldType.CheckboxWithTextList:
            case EntryTypeFieldType.CustomComputed:
            case EntryTypeFieldType.Date:
            case EntryTypeFieldType.Drug:
            case EntryTypeFieldType.DrugAllergy:
            case EntryTypeFieldType.LaboratoryTests:
            case EntryTypeFieldType.LegacyData:
            case EntryTypeFieldType.Link:
            case EntryTypeFieldType.NestedEntryType:
            case EntryTypeFieldType.OptionalText:
            case EntryTypeFieldType.Pregnancy:
            case EntryTypeFieldType.Referrals:
            case EntryTypeFieldType.RenderedService:
            case EntryTypeFieldType.Scale:
            case EntryTypeFieldType.SelectionButtons:
            case EntryTypeFieldType.TextList:
            case EntryTypeFieldType.Toggle:
            case EntryTypeFieldType.MedicalCases:
            case EntryTypeFieldType.Text:
            case EntryTypeFieldType.Number:
            case EntryTypeFieldType.String:
            case EntryTypeFieldType.UsedMedications: {
                const fieldValue = this.entry.getField(field.name);
                value = fieldValue.getPreview();
                isHtml = fieldValue.hasHtmlPreview;

                break;
            }
            case EntryTypeFieldType.Vaccinations: {
                type = EntryTypeFieldType.Vaccinations;
                vaccinationData = this.makeEntryVaccinationsFieldParams();

                break;
            }
            case EntryTypeFieldType.Select: {
                const entryField = this.entry.getField(field.name);
                const fieldValue = entryField.getValue();
                const options = this.makeOptionParams(field.dictionary);

                value = options.find(({ value }) => value === fieldValue)?.label;

                break;
            }
            case EntryTypeFieldType.MultiSelect: {
                const entryField = this.entry.getField<unknown, string>(field.name);
                const fieldValues = entryField.getValue();

                // Legacy imports may store '' here, which passes `?? []` checks, hence the explicit guard
                if (!Array.isArray(fieldValues)) {
                    break;
                }

                const selectedValues = fieldValues.map((it: { value: string }) => it.value);
                const options = this.makeOptionParams(field.dictionary);

                value = options
                    .filter(({ value }) => isDefined(value) && selectedValues.includes(value + ''))
                    .map(it => it.label)
                    .join(', ');

                break;
            }
            case EntryTypeFieldType.Table: {
                type = EntryTypeFieldType.Table;
                tableData = this.makeEntryTableFieldParams(field);

                break;
            }

            case EntryTypeFieldType.Computed: {
                const fieldValue = this.entry.computedFields.get(field.name);
                const fieldType = this.entry.type.getFieldByName(field.name);
                value = fieldType?.format(fieldValue) ?? '';

                break;
            }
            case EntryTypeFieldType.EntryMedicalCases:
            case EntryTypeFieldType.EntryEpisodes:
            case EntryTypeFieldType.EpisdodesDiagnosis:
            case EntryTypeFieldType.EpisdodesReferrals:
            case EntryTypeFieldType.EpisdodesDrugs:
            case EntryTypeFieldType.EpisdodesRecommendations:
            case EntryTypeFieldType.MaterialAct:
            case EntryTypeFieldType.EpisdodesPlanDescription:
            case EntryTypeFieldType.EpisdodesTasks: {
                break;
            }
            default: {
                const neverValue: never = field.type;
                console.warn('Uncnown field type', neverValue);
            }
        }

        return {
            value,
            isHtml,
            tableData,
            vaccinationData,
            dentalData,
            fieldOptions,
            type,
            title: titleOverride ?? field.title,
        };
    }

    private static getDentalFieldValue(name: string): DentalFieldData | undefined {
        return (this.entry.getField(name) as DentalField)?.getValue();
    }

    private static makeEntryTableFieldParams(field: EntryTypeField): EntryPrintFormTableCellParams[][] {
        const tableField = this.entry.getField(field.name) as TableField;
        const entryTypeField = this.entry.entryType.getFieldByName(field.name);
        const tableValues = tableField.getValue();
        assertIsDefined(entryTypeField, `Table field ${field.name} belongs to the entry type`);
        assertIsDefined(entryTypeField.data.table, `Table field ${field.name} has table data`);

        return this.makeTableFieldParams(entryTypeField.data.table.cells, tableValues);
    }

    private static makeEntryVaccinationsFieldParams(): EntryPrintFormVaccinationParams[] {
        const vaccinationList = store.ehr.vaccinationList;
        const vaccinations = vaccinationList.getFilteredValues({ parentEntryId: store.entry.id });

        return vaccinations.map(it => {
            const vaccinationData: EntryPrintFormPairParams[] = [
                { title: translate('stage'), value: it.stage },
                { title: translate('drugDoseSeries'), value: `${it.vaccine}, ${it.dose}, ${it.series}` },
                {
                    title: translate('area'),
                    value: `${it.injectionSite ? `${it.injectionSite}, ` : ''}${it.deliveryMethod}`,
                },
            ];

            if (it.expirationDate) {
                vaccinationData.unshift({
                    title: translate('expirationDate'),
                    value: DateHelper.format(it.expirationDate, DateFormat.DateWithDotSeparator),
                });
            }

            if (it.reaction) {
                vaccinationData.push({ title: translate('reaction'), value: it.reaction });
            }

            if (it.comment) {
                vaccinationData.push({
                    title: translate('comment'),
                    value: it.comment,
                    preserveWhitespace: true,
                });
            }

            return {
                title: it.disease,
                vaccinationData,
            };
        });
    }

    private static makeTableFieldParams(
        cells: TableCellParams[][],
        tableValues: TableData,
    ): EntryPrintFormTableCellParams[][] {
        const rowsWithValues: EntryPrintFormTableCellParamsWithName[][] = [...cells];

        for (let i = 0; i < rowsWithValues.length; i++) {
            for (let j = 0; j < rowsWithValues[i].length; j++) {
                const item = rowsWithValues[i][j];
                if (item.name && tableValues[item.name]) {
                    item.value = tableValues[item.name];
                }
            }
        }

        return rowsWithValues;
    }

    private static makeEntryLabTestBlockParams(): EntryPrintFormBlockData[] {
        const serviceList = store.serviceStore.serviceList;
        const laboratoryTestResults = this.entry.getFieldValue<LaboratoryTestResult[]>(
            EntryTypeFieldName.LaboratoryTestResults,
        );
        return laboratoryTestResults
            .filter(result => !!result.params)
            .map(result => {
                const service = serviceList.get(result.serviceId);
                const fields = this.makeFieldDataFromLaboratoryResult(result);

                if (!!fields.length && fields[0].title?.toLowerCase() === service?.name.toLowerCase()) {
                    fields[0].title = undefined;
                }

                const title = isDefined(service)
                    ? `${service.name} ${
                          result.status === LaboratoryTestStatus.InProgress
                              ? translate('inProgress')
                              : result.status === LaboratoryTestStatus.Canceled
                                ? translate('canceled')
                                : ''
                      }`
                    : undefined;
                return {
                    title,
                    fields,
                };
            });
    }

    private static makeFieldDataFromLaboratoryResult({ params }: LaboratoryTestResult): EntryPrintFormFieldsData[] {
        const groups = LaboratoryTestTableDataHelper.groupResult(params);
        const groupsNames = Object.keys(groups);

        return groupsNames.map(title => {
            const groupParams = groups[title];
            const namesWithValues = LaboratoryTestTableDataHelper.getNamesWithValues(groupParams);
            const cells = [
                LaboratoryTestTableDataHelper.createTitleCells(namesWithValues),
                ...LaboratoryTestTableDataHelper.createDataCells(namesWithValues, groupParams.length),
            ];
            const tableData = this.makeTableFieldParams(
                cells,
                LaboratoryTestTableDataHelper.createTableData(namesWithValues, groupParams),
            );

            return {
                title,
                tableData,
                type: EntryTypeFieldType.Table,
            };
        });
    }

    private static makeEntryMedicalCasesBlockParams(): EntryPrintFormBlockData[] {
        const { medicalCases } = this.entry;
        return medicalCases.values.flatMap(medicalCase => [
            this.makeEntryMedicalCaseTitleBlockData(medicalCase),
            this.makeEntryMedicalCaseTreatmentPlanBlockData(medicalCase),
        ]);
    }

    private static makeEntryMedicalCaseTitleBlockData(medicalCase: MedicalCase): EntryPrintFormBlockData {
        const title = `${translate('diagnosisTreatmentPlanByVisit')}: ${medicalCase.name}`;
        const { createdAt, closedAt, managerId, diagnoses } = medicalCase.dto;
        const manager = managerId ? getCachedUserById(queryClient, managerId) : undefined;
        const doctor = manager ? getFullName(manager) : '-';
        return {
            title,
            isBigTitle: true,
            fields: [
                {
                    title: translate('status'),
                    value: `${translate(medicalCase.isClosed ? 'close' : 'open')} ${DateHelper.format(
                        medicalCase.isClosed ? closedAt : createdAt,
                        DateFormat.DateWithMonthString,
                    )}`,
                    fieldOptions: {
                        occupiedLineWidth: 'TwoPerLineField',
                    },
                },
                {
                    title: translate('episodeManager'),
                    value: doctor,
                    fieldOptions: {
                        occupiedLineWidth: 'TwoPerLineField',
                    },
                },
                ...this.makeMedicalCaseDiagnosis(diagnoses),
            ],
        };
    }

    private static makeMedicalCaseDiagnosis(diagnoses: DiagnosisData[]): EntryPrintFormFieldsData[] {
        return diagnoses.flatMap(({ icdId, clinicalDiagnosis, authorId }) => {
            const diagnose = store.dictionary.getDictionary(DictionaryNames.Mkb10).get(icdId);

            return [
                {
                    title: translate('accordingToICD'),
                    value: `${diagnose?.value ?? ''} ${diagnose?.title ?? ''}`,
                },
                {
                    title: translate('descriptionOfDiagnosis'),
                    value: clinicalDiagnosis,
                },
            ];
        });
    }

    private static makeEntryMedicalCaseTreatmentPlanBlockData(medicalCase: MedicalCase): EntryPrintFormBlockData {
        const {
            id: medicalCaseId,
            recommendationList,
            outwardReferralList,
            hasReferrals,
            referralIds,
            dto: { drugIds },
        } = medicalCase;
        const { drugList, id, isSigned, isOnPrint, creationTime, visitTime, signedAt, drugHistoryList } = this.entry;
        const tasks = taskStore.taskList.getFilteredValues({
            medicalCaseId,
            excludedStatus: [TaskStatus.Cancelled],
        });
        const referralField = this.entry.getEntryField(EntryTypeFieldType.Referrals) as ReferralField;

        const { referrals } = hasReferrals
            ? splitReferralsByReservation(referralField, referralIds, { entryId: id, isOnPrint })
            : { referrals: [] };
        const { items } = makeGroupedItems(
            {
                referrals,
                reservations: [],
                entries: [],
                recommendations: isSigned
                    ? recommendationList.values.filter(it => it.parentEntryId === id)
                    : recommendationList.values,
                tasks,
                outwardReferrals: filterEntryOutwardReferrals(outwardReferralList.values, id),
                ...getFilteredDrugs(
                    { drugList, drugs: drugIds },
                    {
                        entryId: id,
                        isOnPrint,
                        drugHistoryList,
                        fromSignedEntry: isSigned,
                        actualOn: signedAt ?? creationTime,
                    },
                ),
            },
            { fromEhr: false, isOnPrint, entryDate: visitTime ?? creationTime },
        );

        const fields = items.map(item => {
            const data: EntryPrintFormFieldsData = { title: item.type, value: item.id };
            if (item.type === ItemType.Task) {
                const { dictionary } = store;
                const task = taskStore.taskList.getExisting(item.id);
                const taskTypeDictionary = dictionary.getDictionary(FrontendDictionaryNames.TaskType);

                const { title: taskTypeTitle } = taskTypeDictionary.getByValue(item.type) ?? {};

                data.value = `${translate('task')} ${formatItemDate(item)}`;
                data.title = task.description || taskTypeTitle || '-';
            } else if (item.type === ItemType.Recommendation) {
                const { isImportant, comment } = recommendationList.getExisting(item.id);
                data.value = `${translate('recommendation')} ${formatItemDate(item)}`;
                data.title = `${isImportant ? '🚩 ' : ''}${comment}`;
            } else if (item.type === ItemType.OutwardReferral) {
                const { name, comment } = outwardReferralList.getExisting(item.id);
                data.value = `${translate('referral')} ${formatItemDate(item)}`;
                data.title = `${name}\n${comment}`;
            } else if (item.type === ItemType.Referral) {
                data.value = `${translate('referral')} ${formatItemDate(item)}`;
                data.title = referralField.getRedesignItemTitle(item.id);
            } else if (item.type === ItemType.Drug) {
                const { tradeNameIds, customTradeNames, activeSubstance } = drugList.getExisting(item.id);
                data.value = `${translate('medicine')} ${formatItemDate(item)}`;
                data.title = `${DrugField.getTradeNameTitles(
                    tradeNameIds ?? [],
                    customTradeNames ?? [],
                )} ${DrugField.getActiveSubstanceTitle(activeSubstance)}`;
            }

            return data;
        });
        return {
            fields,
            title: translate('treatmentPlan'),
        };
    }

    // @ts-expect-error
    private static makeMedicalCaseRecommendations(recommendationList: RecommendationList): EntryPrintFormFieldsData[] {
        return recommendationList.values.map(recommendation => {
            const period = this.makePeriod(recommendation.start, recommendation.end);

            return {
                title: `${translate('recommendation')}. ${period}`,
                value: recommendation.comment,
            };
        });
    }

    // @ts-expect-error
    private static makeMedicalCaseDrugs(drugIds: string[]): EntryPrintFormFieldsData[] {
        // @ts-expect-error
        return drugIds.map(id => {
            const drug = this.entry.drugList.getExisting(id);

            const tradeNamesDictionary = store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);
            const activeSubstanceDictionary = store.dictionary.getDictionary(DictionaryNames.ElementDrug);

            const tradeNames = drug.tradeNameIds?.map(id => tradeNamesDictionary.get(id)?.title);
            const customTradeNames = drug.customTradeNames;

            const activeSubstance = activeSubstanceDictionary.get(drug.activeSubstance)?.title;

            const tradeNamesString = compact([tradeNames?.join('. '), customTradeNames?.join('. ')]).join(' / ');

            const period = this.makePeriod(drug.start, drug.end);
            const drugAuthor = getCachedUserById(queryClient, drug.authorId);
            const doctorString = drugAuthor ? `${translate('appointedByDoctor')}: ${getFullName(drugAuthor)}` : '';

            return {
                title: `${translate('medicine')}. ${period}`,
                value: {
                    tradeNamesString,
                    activeSubstance,
                    doctor: doctorString,
                    frequencyIfReception: drug.frequencyIfReception,
                },
            };
        });
    }

    private static makePeriod(start: Optional<DateType>, end?: DateType): string {
        const startDate = `${translate('from')} ${DateHelper.formatIfNotNil(start, DateFormat.DayWithMonthString)}`;
        const endDate = end
            ? `${translate('to').toLowerCase()} ${DateHelper.formatIfNotNil(end, DateFormat.DayWithMonthString)}`
            : '';

        return [startDate, endDate].join(' ');
    }

    private static makeOptionParams(dictionaryName?: string): OptionType[] {
        return dictionaryName
            ? this.dictionary.getDictionary(dictionaryName).values.map(dictionary => ({
                  value: dictionary.id,
                  label: dictionary.title,
              }))
            : [];
    }
}
